import React, { FC, ReactText, useCallback, useMemo, useRef } from 'react';
import { Dropdown, MenuProps } from 'antd';
import { Store } from 'antd/lib/form/interface';
import { PaginationConfig } from 'antd/lib/pagination';
import { SorterResult, TableCurrentDataSource } from 'antd/lib/table/interface';
import moment from 'moment';
import {
  Area,
  AreaChart,
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { exportReportToExcel, exportReportToPdf } from '@core/utils/methods';
import {
  NewClosedProjectsReportResponse,
  OptionProps,
  ReportsFilters,
} from '@models/interfaces';
import { CustomTable } from '@shared/components';
import { PrimaryButton } from '@shared/modules';
import type { ReportsSummaryMetric } from '../../models';
import {
  FULL_FILTER_CAPTION,
  getNewClosedProjectsColumns,
  getNewClosedProjectsReportRowKey,
  hasNewClosedProjectsSeriesData,
  NEW_CLOSED_PROJECTS_AREA_SERIES,
  NEW_CLOSED_PROJECTS_CHART_COLORS,
} from '../../models';
import { ReportChartCard } from '../report-chart-card';
import { ReportsSummary } from '../reports-summary';

import './styles.scss';

interface NewClosedProjectsReportProps {
  data?: NewClosedProjectsReportResponse;
  filters: ReportsFilters;
  onTableChange: (
    pagination: PaginationConfig,
    tableFilters: Record<string, ReactText[] | null>,
    sorter: SorterResult<Store> | SorterResult<Store>[],
    extra: TableCurrentDataSource<Store>,
  ) => void;
  sortByOptions: OptionProps[];
}

const CHART_GRID_STROKE = 'rgba(255,255,255,0.12)';
const CHART_AXIS_STROKE = 'rgba(255,255,255,0.65)';

export const NewClosedProjectsReport: FC<NewClosedProjectsReportProps> = ({
  data,
  filters,
  onTableChange,
  sortByOptions,
}: NewClosedProjectsReportProps) => {
  const chartsRef = useRef<HTMLDivElement>(null);
  const rows = data?.table?.items ?? [];
  const total = data?.table?.total ?? 0;
  const series = data?.charts?.series ?? [];
  const hasSeriesData = hasNewClosedProjectsSeriesData(series);
  const columns = useMemo(
    () => getNewClosedProjectsColumns(filters, sortByOptions),
    [filters, sortByOptions],
  );
  const metrics: ReportsSummaryMetric[] = [
    {
      caption: FULL_FILTER_CAPTION,
      label: 'Total New',
      value: data?.summary?.newProjects ?? 0,
    },
    {
      caption: FULL_FILTER_CAPTION,
      label: 'Total Closed',
      value: data?.summary?.closedProjects ?? 0,
    },
    {
      caption: FULL_FILTER_CAPTION,
      label: 'Net Change',
      value: data?.summary?.netChange ?? 0,
    },
  ];

  const onExportExcel = useCallback((): void => {
    exportReportToExcel({
      columns,
      fileName: `New-Closed-Projects-${moment().format(
        'YYYY-MM-DD-HHmm',
      )}.xlsx`,
      metrics,
      reportTitle: 'New Closed Projects',
      rows,
    });
  }, [columns, metrics, rows]);

  const onExportPdf = useCallback((): void => {
    exportReportToPdf({
      chartsElement: chartsRef.current,
      columns,
      fileName: `New-Closed-Projects-${moment().format('YYYY-MM-DD-HHmm')}.pdf`,
      metrics,
      reportTitle: 'New Closed Projects',
      rows,
    });
  }, [columns, metrics, rows]);

  const exportMenu: MenuProps = {
    items: [
      { key: 'excel', label: 'Export as Excel', onClick: onExportExcel },
      { key: 'pdf', label: 'Export as PDF', onClick: onExportPdf },
    ],
  };

  return (
    <div className="prov-new-closed-projects-report">
      <div className="prov-new-closed-projects-report__toolbar">
        <p className="prov-new-closed-projects-report__description">
          Counts projects opened (&quot;new,&quot; based on when the project
          record was created) versus projects finished (&quot;closed,&quot;
          based on the actual date of the completion/OnAir task) within a
          period, bucketed by month or quarter.
        </p>
        <Dropdown disabled={!rows.length} menu={exportMenu}>
          <PrimaryButton title="Export ▾" />
        </Dropdown>
      </div>

      <ReportsSummary metrics={metrics} />

      <div className="prov-new-closed-projects-report__charts" ref={chartsRef}>
        <ReportChartCard
          hasData={hasSeriesData}
          title="New vs Closed Projects Over Time"
        >
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={series}
              margin={{ bottom: 8, left: 8, right: 24, top: 8 }}
            >
              <CartesianGrid stroke={CHART_GRID_STROKE} />
              <XAxis dataKey="period" stroke={CHART_AXIS_STROKE} />
              <YAxis allowDecimals={false} stroke={CHART_AXIS_STROKE} />
              <Tooltip />
              <Legend />
              {NEW_CLOSED_PROJECTS_AREA_SERIES.map(
                ({ dataKey, fill, name }) => (
                  <Area
                    dataKey={dataKey}
                    fill={fill}
                    fillOpacity={0.35}
                    key={dataKey}
                    name={name}
                    stackId="projects"
                    stroke={fill}
                    type="monotone"
                  />
                ),
              )}
            </AreaChart>
          </ResponsiveContainer>
        </ReportChartCard>

        <ReportChartCard
          hasData={hasSeriesData}
          title="New, Closed, and Cumulative Net Change"
        >
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={series}
              margin={{ bottom: 8, left: 8, right: 24, top: 8 }}
            >
              <CartesianGrid stroke={CHART_GRID_STROKE} />
              <XAxis dataKey="period" stroke={CHART_AXIS_STROKE} />
              <YAxis
                allowDecimals={false}
                stroke={CHART_AXIS_STROKE}
                yAxisId="counts"
              />
              <YAxis
                allowDecimals={false}
                orientation="right"
                stroke={NEW_CLOSED_PROJECTS_CHART_COLORS.cumulative}
                yAxisId="cumulative"
              />
              <Tooltip />
              <Legend />
              <Bar
                dataKey="newCount"
                fill={NEW_CLOSED_PROJECTS_CHART_COLORS.new}
                name="New Projects"
                yAxisId="counts"
              />
              <Bar
                dataKey="closedCount"
                fill={NEW_CLOSED_PROJECTS_CHART_COLORS.closed}
                name="Closed Projects"
                yAxisId="counts"
              />
              <Line
                dataKey="cumulativeNetChange"
                dot={false}
                name="Cumulative Net Change"
                stroke={NEW_CLOSED_PROJECTS_CHART_COLORS.cumulative}
                strokeWidth={2}
                type="monotone"
                yAxisId="cumulative"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </ReportChartCard>
      </div>

      <CustomTable
        className="prov-new-closed-projects-report__table"
        columns={columns}
        dataSource={rows}
        onChange={onTableChange}
        pagination={{
          current: filters.page,
          pageSize: filters.size,
          total,
        }}
        rowKey={getNewClosedProjectsReportRowKey}
        scroll={{ x: 2600, y: 'auto' }}
      />
    </div>
  );
};
